import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  User, Camera, Heart, Settings, Eye, Edit, Save, X, Plus, Trash2,
  MapPin, Mail, Calendar, Loader, Check, AlertCircle, Star, TrendingUp,
  Users, MessageSquare, Target, Shield, Crown, Brain
} from 'lucide-react';
import QuickPhotoManager from './QuickPhotoManager';
import PersonalityProfile from './PersonalityProfile';

const ProfileManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [showPhotoManager, setShowPhotoManager] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [stats, setStats] = useState({
    profileViews: 0,
    likes: 0,
    matches: 0,
    responseRate: 0
  });

  useEffect(() => {
    loadProfile();
    loadStats();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch both basic profile and detailed profile (from Profile Optimization)
      const [userResponse, detailedResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/me`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/detailed-profile`, { headers }).catch(() => ({ data: null }))
      ]);
      
      // Merge data - detailed profile takes priority for bio, headline, interests
      const userData = userResponse.data;
      const detailedResponse_data = detailedResponse.data;
      // The detailed-profile endpoint returns { profile: {...}, exists: true }
      const detailedProfile = detailedResponse_data?.profile || null;
      
      const mergedProfile = {
        ...userData,
        // Use detailed profile data if available (from Profile Optimization)
        bio: detailedProfile?.bio || userData.bio,
        headline: detailedProfile?.headline || userData.headline,
        interests: detailedProfile?.interests || userData.interests || [],
        relationship_goals: detailedProfile?.relationship_goals || userData.relationship_goals,
        // Include detailed profile flag
        has_detailed_profile: !!detailedProfile
      };
      
      setProfile(mergedProfile);
      console.log('📋 Profile loaded (merged with detailed profile):', mergedProfile);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch real stats from the API
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/me/stats`,
        { headers }
      );
      
      setStats({
        profileViews: response.data.profileViews || 0,
        likes: response.data.likes || 0,
        matches: response.data.matches || 0,
        responseRate: response.data.responseRate || 0
      });
      
      console.log('📊 Profile stats loaded:', response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      // Keep default zeros on error
      setStats({
        profileViews: 0,
        likes: 0,
        matches: 0,
        responseRate: 0
      });
    }
  };

  const calculateProfileCompletion = () => {
    if (!profile) return 0;
    
    let completed = 0;
    let total = 10;

    if (profile.name) completed++;
    if (profile.age) completed++;
    if (profile.gender) completed++;
    if (profile.bio && profile.bio.length > 50) completed++;
    if (profile.location) completed++;
    if (profile.interests && profile.interests.length >= 3) completed++;
    if (profile.photos && profile.photos.length >= 3) completed++;
    if (profile.headline) completed++;
    if (profile.relationship_goals) completed++;
    if (profile.has_personality_test) completed++;

    return Math.round((completed / total) * 100);
  };

  const handleSave = async (field, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Save to main user profile
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/me`,
        { [field]: value },
        { headers }
      );
      
      // Also sync bio/headline/interests to detailed_profiles (via apply-optimization endpoint)
      // This keeps ProfileManagement in sync with Profile Optimization page
      if (['bio', 'headline', 'interests'].includes(field)) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/premium/apply-optimization`,
            { type: field, value: value },
            { headers }
          );
          console.log(`✅ Synced ${field} to detailed profile (Profile Optimization)`);
        } catch (syncError) {
          // Sync may fail for free users - that's ok, main profile is still saved
          console.log(`Note: Profile Optimization sync skipped (${syncError.response?.status || 'error'})`);
        }
      }
      
      setProfile({ ...profile, [field]: value });
      setEditMode({ ...editMode, [field]: false });
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <User className="w-4 h-4" /> },
    { id: 'photos', label: 'Photos', icon: <Camera className="w-4 h-4" /> },
    { id: 'about', label: 'About Me', icon: <Heart className="w-4 h-4" /> },
    { id: 'preferences', label: 'Preferences', icon: <Target className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const completionPercentage = calculateProfileCompletion();

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-white/20 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center overflow-hidden border-2 border-white/40">
                {profile?.photos?.[0] ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${profile.photos[0].url}`} 
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white drop-shadow-sm">{profile?.name || 'Complete Your Profile'}</h2>
                <p className="text-white/90 font-medium">{profile?.email}</p>
                <Badge className="mt-1 bg-purple-500/40 text-white font-semibold border-purple-400/50">
                  {(profile?.subscription_tier || profile?.subscription?.tier || 'Free').toUpperCase()} Member
                </Badge>
              </div>
            </div>
            
            <Button
              onClick={() => setShowPreview(true)}
              variant="outline"
              className="text-white border-white/50 hover:bg-white/20 font-medium"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Profile
            </Button>
          </div>

          {/* Profile Completion */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold drop-shadow-sm">Profile Completion</span>
              <span className="text-white font-bold">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-3" />
            {completionPercentage < 100 && (
              <p className="text-white/80 text-sm mt-2 font-medium">
                Complete your profile to get better matches and more visibility
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-300 bg-white/50 backdrop-blur-sm rounded-t-lg px-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center space-x-2 px-4 py-3 font-semibold transition-colors
              ${activeTab === tab.id
                ? 'text-purple-700 border-b-2 border-purple-600 bg-white/60'
                : 'text-gray-700 hover:text-purple-600 hover:bg-white/40'
              }
            `}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <OverviewTab 
            profile={profile}
            stats={stats}
            completionPercentage={completionPercentage}
            onEditClick={() => setActiveTab('about')}
          />
        )}

        {activeTab === 'photos' && (
          <PhotosTab 
            profile={profile}
            onPhotosUpdated={loadProfile}
            onManagePhotos={() => setShowPhotoManager(true)}
          />
        )}

        {activeTab === 'about' && (
          <AboutMeTab 
            profile={profile}
            editMode={editMode}
            setEditMode={setEditMode}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === 'preferences' && (
          <PreferencesTab 
            profile={profile}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab 
            profile={profile}
            onSave={handleSave}
          />
        )}
      </div>

      {/* Photo Manager Modal */}
      {showPhotoManager && (
        <QuickPhotoManager
          onClose={() => setShowPhotoManager(false)}
          onPhotosUpdated={(photos) => {
            loadProfile();
            setShowPhotoManager(false);
          }}
        />
      )}

      {/* Profile Preview Modal */}
      {showPreview && (
        <ProfilePreviewModal
          profile={profile}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ profile, stats, completionPercentage, onEditClick }) => {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Quick Stats */}
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
            Profile Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 font-medium">Profile Views</span>
            <span className="font-bold text-xl text-gray-900">{stats.profileViews}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700 font-medium">Likes Received</span>
            <span className="font-bold text-xl text-gray-900">{stats.likes}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700 font-medium">Active Matches</span>
            <span className="font-bold text-xl text-gray-900">{stats.matches}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700 font-medium">Response Rate</span>
            <span className="font-bold text-xl text-green-600">{stats.responseRate}%</span>
          </div>
        </CardContent>
      </Card>

      {/* Profile Strength */}
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <Star className="w-5 h-5 mr-2 text-yellow-500" />
            Profile Strength
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">Overall</span>
                <span className="text-sm font-bold text-gray-900">{completionPercentage}%</span>
              </div>
              <Progress value={completionPercentage} className="h-2" />
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-200">
              <p className="text-sm font-bold text-gray-800">To improve your profile:</p>
              {completionPercentage < 100 && (
                <ul className="text-sm text-gray-700 space-y-1 font-medium">
                  {!profile?.headline && <li>• Add a catchy headline</li>}
                  {(!profile?.bio || profile.bio.length < 50) && <li>• Write a detailed bio (50+ characters)</li>}
                  {(!profile?.photos || profile.photos.length < 3) && <li>• Upload at least 3 photos</li>}
                  {(!profile?.interests || profile.interests.length < 3) && <li>• Add at least 3 interests</li>}
                  {!profile?.has_personality_test && <li>• Complete personality test</li>}
                </ul>
              )}
              {completionPercentage === 100 && (
                <p className="text-sm text-green-600 font-semibold">✓ Your profile is complete!</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="md:col-span-2 bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              onClick={onEditClick}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
            <Button
              onClick={() => window.location.href = '/connections'}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <Users className="w-4 h-4 mr-2" />
              Your Connections
            </Button>
            <Button
              onClick={() => window.location.href = '/matches'}
              variant="outline"
            >
              <Heart className="w-4 h-4 mr-2" />
              View Matches
            </Button>
            <Button
              onClick={() => window.location.href = '/chat'}
              variant="outline"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Personality Profile */}
      <div className="md:col-span-2">
        <PersonalityProfile />
      </div>
    </div>
  );
};

// Photos Tab Component
const PhotosTab = ({ profile, onPhotosUpdated, onManagePhotos }) => {
  const photos = profile?.photos || [];

  return (
    <div className="space-y-6">
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-gray-800">
              <Camera className="w-5 h-5 mr-2 text-purple-600" />
              Your Photos ({photos.length}/6)
            </CardTitle>
            <Button
              onClick={onManagePhotos}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Manage Photos
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">No Photos Yet</h3>
              <p className="text-gray-700 mb-6 font-medium">
                Add photos to make your profile more attractive
              </p>
              <Button
                onClick={onManagePhotos}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                <Camera className="w-4 h-4 mr-2" />
                Upload Your First Photo
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt={`Photo ${index + 1}`}
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                  {photo.is_primary && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      Primary
                    </div>
                  )}
                  {index === 0 && !photo.is_primary && (
                    <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      Profile Picture
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Photo Tips:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use clear, well-lit photos where your face is visible</li>
              <li>• Show your personality with variety (hobbies, travel, etc.)</li>
              <li>• Smile! Friendly photos get more matches</li>
              <li>• Avoid group photos as your primary picture</li>
              <li>• Upload at least 3-5 photos for best results</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// About Me Tab Component  
const AboutMeTab = ({ profile, editMode, setEditMode, onSave, saving }) => {
  const [formData, setFormData] = useState({
    headline: profile?.headline || '',
    bio: profile?.bio || '',
    interests: profile?.interests || [],
    relationship_goals: profile?.relationship_goals || ''
  });

  const availableInterests = [
    'Hiking', 'Reading', 'Cooking', 'Travel', 'Music', 'Movies', 'Fitness',
    'Photography', 'Art', 'Gaming', 'Yoga', 'Dancing', 'Coffee', 'Wine',
    'Sports', 'Pets', 'Nature', 'Technology', 'Fashion', 'Food'
  ];

  const toggleInterest = (interest) => {
    const newInterests = formData.interests.includes(interest)
      ? formData.interests.filter(i => i !== interest)
      : [...formData.interests, interest];
    setFormData({ ...formData, interests: newInterests });
  };

  return (
    <div className="space-y-6">
      {/* Headline */}
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800">Headline</CardTitle>
        </CardHeader>
        <CardContent>
          {editMode.headline ? (
            <div className="space-y-3">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                placeholder="e.g., Adventure seeker | Coffee enthusiast | Dog lover"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                maxLength={100}
              />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 font-medium">{formData.headline.length}/100</span>
                <div className="space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode({ ...editMode, headline: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onSave('headline', formData.headline)}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-gray-800 font-medium">{profile?.headline || 'Add a catchy headline'}</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditMode({ ...editMode, headline: true })}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bio */}
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800">About Me</CardTitle>
        </CardHeader>
        <CardContent>
          {editMode.bio ? (
            <div className="space-y-3">
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell people about yourself, your interests, what you're looking for..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg min-h-[150px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                maxLength={500}
              />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 font-medium">{formData.bio.length}/500</span>
                <div className="space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode({ ...editMode, bio: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onSave('bio', formData.bio)}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <p className="text-gray-800 whitespace-pre-line font-medium">
                {profile?.bio || 'Tell people about yourself...'}
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditMode({ ...editMode, bio: true })}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Interests */}
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800">Interests & Hobbies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {availableInterests.map((interest) => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`
                    px-4 py-2 rounded-full text-sm font-semibold transition-colors
                    ${formData.interests.includes(interest)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }
                  `}
                >
                  {interest}
                </button>
              ))}
            </div>
            <Button
              onClick={() => onSave('interests', formData.interests)}
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Interests'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Relationship Goals */}
      <Card>
        <CardHeader>
          <CardTitle>What I'm Looking For</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <select
              value={formData.relationship_goals}
              onChange={(e) => setFormData({ ...formData, relationship_goals: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">Select...</option>
              <option value="casual_dating">Casual Dating</option>
              <option value="serious_relationship">Serious Relationship</option>
              <option value="friendship">Friendship</option>
              <option value="open_to_anything">Open to Anything</option>
              <option value="marriage">Marriage</option>
            </select>
            <Button
              onClick={() => onSave('relationship_goals', formData.relationship_goals)}
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Preferences Tab Component
const PreferencesTab = ({ profile, onSave, saving }) => {
  const [prefs, setPrefs] = useState({
    age_range_min: profile?.age_range_min || 18,
    age_range_max: profile?.age_range_max || 65,
    interested_in: profile?.interested_in || [],
    preferred_distance_miles: profile?.preferred_distance_miles || 25,
    enable_location_matching: profile?.enable_location_matching || false
  });

  const handleSavePrefs = async () => {
    for (const [key, value] of Object.entries(prefs)) {
      await onSave(key, value);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Age Range</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Age: {prefs.age_range_min}
              </label>
              <input
                type="range"
                min="18"
                max="80"
                value={prefs.age_range_min}
                onChange={(e) => setPrefs({ ...prefs, age_range_min: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Age: {prefs.age_range_max}
              </label>
              <input
                type="range"
                min="18"
                max="80"
                value={prefs.age_range_max}
                onChange={(e) => setPrefs({ ...prefs, age_range_max: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Interested In</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {['men', 'women', 'non-binary'].map((gender) => (
              <label key={gender} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={prefs.interested_in.includes(gender)}
                  onChange={(e) => {
                    const newInterests = e.target.checked
                      ? [...prefs.interested_in, gender]
                      : prefs.interested_in.filter(g => g !== gender);
                    setPrefs({ ...prefs, interested_in: newInterests });
                  }}
                  className="w-5 h-5"
                />
                <span className="capitalize">{gender}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Distance Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={prefs.enable_location_matching}
                onChange={(e) => setPrefs({ ...prefs, enable_location_matching: e.target.checked })}
                className="w-5 h-5"
              />
              <span>Enable location-based matching</span>
            </label>
            
            {prefs.enable_location_matching && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Distance: {prefs.preferred_distance_miles} miles
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={prefs.preferred_distance_miles}
                  onChange={(e) => setPrefs({ ...prefs, preferred_distance_miles: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={handleSavePrefs}
        disabled={saving}
        className="w-full bg-purple-600 hover:bg-purple-700"
      >
        {saving ? 'Saving...' : 'Save All Preferences'}
      </Button>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ profile, onSave }) => {
  return (
    <div className="space-y-6">
      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-800">
            <Shield className="w-5 h-5 mr-2 text-purple-600" />
            Privacy & Visibility
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800">Profile Visibility</p>
                <p className="text-sm text-gray-600 font-medium">Show your profile in search results</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800">Show Distance</p>
                <p className="text-sm text-gray-600 font-medium">Display your distance from matches</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-gray-800">Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Member Since</span>
              <span className="font-bold text-gray-900">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Account Type</span>
              <Badge className="bg-purple-100 text-purple-800 font-bold">
                {(profile?.subscription_tier || profile?.subscription?.tier || 'Free').charAt(0).toUpperCase() + (profile?.subscription_tier || profile?.subscription?.tier || 'Free').slice(1)}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Email Verified</span>
              <span className="text-green-600 flex items-center font-bold">
                <Check className="w-4 h-4 mr-1" />
                Verified
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Profile Preview Modal Component
const ProfilePreviewModal = ({ profile, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Profile Preview</CardTitle>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-gray-600">This is how others see your profile</p>
        </CardHeader>
        <CardContent>
          {/* Simplified profile view */}
          <div className="space-y-4">
            {profile?.photos?.[0] && (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${profile.photos[0].url}`}
                alt={profile.name}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="text-2xl font-bold">{profile?.name}, {profile?.age}</h3>
              {profile?.headline && (
                <p className="text-purple-600 font-medium">{profile.headline}</p>
              )}
              {profile?.location && (
                <p className="text-gray-600 flex items-center mt-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  {profile.location}
                </p>
              )}
            </div>
            
            {profile?.bio && (
              <div>
                <h4 className="font-semibold mb-2">About</h4>
                <p className="text-gray-700 whitespace-pre-line">{profile.bio}</p>
              </div>
            )}

            {profile?.interests && profile.interests.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">Interests</h4>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, i) => (
                    <Badge key={i} className="bg-purple-100 text-purple-800">
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileManagement;
